import { useEffect, useRef, useState } from 'react'

/**
 * Reveals an element on scroll via IntersectionObserver. Pair the returned ref
 * with the `.reveal` class and toggle `.visible` from the boolean. The motion
 * itself is gated behind `prefers-reduced-motion` in the app CSS, so this hook
 * stays simple and only tracks intersection.
 */
export function useReveal<T extends HTMLElement = HTMLElement>() {
  const ref = useRef<T>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true)
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return [ref, visible] as const
}
